import { useState, useEffect, useCallback } from 'react';
import { getRandomWord, CATEGORIES } from '../../utils/wordManager';
import { convertToMistyped } from '../../utils/layoutConverter';
import styles from './TrainingScreen.module.css';

const STORAGE_KEY = 'ghbdtn_score';

const loadScore = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (saved && typeof saved.correct === 'number') return saved;
    } catch {}
    return { correct: 0, wrong: 0, streak: 0, bestStreak: 0 };
};

const normalize = (s) => s.toLowerCase().replace(/ё/g, 'е');

const TrainingScreen = ({ customPhrase, onCustomDone }) => {
    const [currentWord, setCurrentWord] = useState(null);
    const [userInput, setUserInput] = useState('');
    const [score, setScore] = useState(loadScore);
    const [currentCategory, setCurrentCategory] = useState('все');
    const [inputState, setInputState] = useState('default');
    const [shake, setShake] = useState(false);
    const [wordKey, setWordKey] = useState(0);
    const [revealed, setRevealed] = useState(false);

    // Phrase mode state
    const [guessedIndices, setGuessedIndices] = useState(new Set());

    const isCustom = !!customPhrase;
    const allCategories = ['все', ...CATEGORIES];
    const isPhrase = currentWord?.text.includes(' ');
    const phraseWords = isPhrase ? currentWord.text.split(' ') : [];
    const allGuessed = isPhrase && guessedIndices.size === phraseWords.length;

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(score));
    }, [score]);

    const loadNewWord = useCallback(() => {
        if (isCustom) {
            setCurrentWord({ text: customPhrase, category: 'custom' });
        } else {
            const word = getRandomWord(currentCategory);
            setCurrentWord(word);
        }
        setUserInput('');
        setInputState('default');
        setRevealed(false);
        setGuessedIndices(new Set());
        setWordKey(prev => prev + 1);
    }, [currentCategory, customPhrase, isCustom]);

    const handleCategoryChange = (category) => {
        setCurrentCategory(category);
    };

    const handleSkip = () => {
        if (revealed || allGuessed) {
            if (isCustom) {
                onCustomDone?.();
            } else {
                loadNewWord();
            }
            return;
        }
        setRevealed(true);
        if (isPhrase) {
            setGuessedIndices(new Set(phraseWords.map((_, i) => i)));
        }
        setScore(prev => ({ ...prev, wrong: prev.wrong + 1, streak: 0 }));
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setUserInput(val);

        if (!val.trim() || revealed) return;
        const input = normalize(val.trim());

        if (isPhrase && !allGuessed) {
            const matchIndex = phraseWords.findIndex(
                (w, i) => !guessedIndices.has(i) && normalize(w) === input
            );
            if (matchIndex !== -1) {
                const newGuessed = new Set(guessedIndices);
                newGuessed.add(matchIndex);
                setGuessedIndices(newGuessed);
                setUserInput('');
                setInputState('default');

                if (newGuessed.size === phraseWords.length) {
                    setInputState('correct');
                    setScore(prev => {
                        const newStreak = prev.streak + 1;
                        return {
                            ...prev,
                            correct: prev.correct + 1,
                            streak: newStreak,
                            bestStreak: Math.max(prev.bestStreak, newStreak)
                        };
                    });
                    if (!isCustom) {
                        setTimeout(loadNewWord, 1200);
                    }
                }
            }
        } else if (!isPhrase && currentWord) {
            if (input === normalize(currentWord.text)) {
                setInputState('correct');
                setScore(prev => {
                    const newStreak = prev.streak + 1;
                    return {
                        ...prev,
                        correct: prev.correct + 1,
                        streak: newStreak,
                        bestStreak: Math.max(prev.bestStreak, newStreak)
                    };
                });
                if (!isCustom) {
                    setTimeout(loadNewWord, 1000);
                }
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleCheck();
        } else if (e.key === 'Tab') {
            e.preventDefault();
            handleSkip();
        }
    };

    const handleCheck = () => {
        if (revealed || allGuessed) {
            if (isCustom) {
                onCustomDone?.();
            } else {
                loadNewWord();
            }
            return;
        }
        if (!userInput.trim()) return;

        const input = normalize(userInput.trim());

        if (isPhrase) {
            // Find first unguessed word that matches
            const matchIndex = phraseWords.findIndex(
                (w, i) => !guessedIndices.has(i) && normalize(w) === input
            );

            if (matchIndex !== -1) {
                const newGuessed = new Set(guessedIndices);
                newGuessed.add(matchIndex);
                setGuessedIndices(newGuessed);
                setUserInput('');
                setInputState('default');

                if (newGuessed.size === phraseWords.length) {
                    // All words guessed
                    setInputState('correct');
                    setScore(prev => {
                        const newStreak = prev.streak + 1;
                        return {
                            ...prev,
                            correct: prev.correct + 1,
                            streak: newStreak,
                            bestStreak: Math.max(prev.bestStreak, newStreak)
                        };
                    });
                    if (!isCustom) {
                        setTimeout(loadNewWord, 1200);
                    }
                }
            } else {
                setInputState('wrong');
                setShake(true);
                setScore(prev => ({ ...prev, wrong: prev.wrong + 1, streak: 0 }));
                setTimeout(() => setShake(false), 400);
            }
        } else {
            // Single word mode
            const isCorrect = input === normalize(currentWord?.text ?? '');

            if (isCorrect) {
                setInputState('correct');
                setScore(prev => {
                    const newStreak = prev.streak + 1;
                    return {
                        ...prev,
                        correct: prev.correct + 1,
                        streak: newStreak,
                        bestStreak: Math.max(prev.bestStreak, newStreak)
                    };
                });
                if (isCustom) {
                    // stay, user clicks "Дальше"
                } else {
                    setTimeout(loadNewWord, 500);
                }
            } else {
                setInputState('wrong');
                setShake(true);
                setScore(prev => ({ ...prev, wrong: prev.wrong + 1, streak: 0 }));
                setTimeout(() => setShake(false), 400);
            }
        }
    };

    useEffect(() => loadNewWord(), [loadNewWord]);

    const renderPhraseDisplay = () => {
        return (
            <div className={styles.phraseDisplay}>
                {phraseWords.map((word, i) => {
                    const guessed = guessedIndices.has(i);
                    return (
                        <span key={i}>
                            <span className={`${styles.phraseWord} ${guessed ? styles.guessed : ''}`}>
                                {guessed ? word : convertToMistyped(word)}
                            </span>
                            {i < phraseWords.length - 1 && ' '}
                        </span>
                    );
                })}
            </div>
        );
    };

    const isDone = isPhrase ? allGuessed : inputState === 'correct';

    return (
        <div className={`${styles.container} ${shake ? styles.shake : ''}`}>
            {!isCustom && (
                <div className={styles.levelSelector}>
                    {allCategories.map(category => (
                        <button
                            key={category}
                            onClick={() => handleCategoryChange(category)}
                            className={`${styles.levelButton} ${
                                currentCategory === category ? styles.active : ''
                            }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>
            )}

            {isCustom && (
                <div className={styles.customBadge}>
                    послание от друга
                </div>
            )}

            <div key={wordKey} className={styles.wordContainer}>
                <div className={styles.task}>
                    {isPhrase ? (
                        renderPhraseDisplay()
                    ) : (
                        <>
                            Переведите: <span className={styles.mistyped}>
                                {currentWord ? convertToMistyped(currentWord.text) : '...'}
                            </span>
                        </>
                    )}
                </div>
                {revealed && !isPhrase && (
                    <div className={styles.answer}>{currentWord?.text}</div>
                )}
            </div>

            <input
                value={userInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                className={`${styles.input} ${styles[inputState]}`}
                placeholder={isPhrase
                    ? `Угадайте слово (${guessedIndices.size}/${phraseWords.length})...`
                    : 'Введите правильный вариант...'
                }
                disabled={revealed || isDone}
                autoFocus
            />

            <div className={styles.buttons}>
                <button onClick={isDone || revealed ? handleSkip : handleCheck} className={styles.button}>
                    {isDone || revealed ? (isCustom ? 'Готово' : 'Дальше') : 'Проверить'}
                </button>
                {!isDone && !revealed && (
                    <button onClick={handleSkip} className={styles.skipButton}>
                        Сдаюсь
                    </button>
                )}
            </div>

            {!isCustom && (
                <div className={styles.scoreRow}>
                    <div className={styles.score}>
                        <div className={styles.scoreCorrect}>{score.correct}</div>
                        <div className={styles.scoreWrong}>{score.wrong}</div>
                        {score.streak > 1 && (
                            <div className={styles.streak}>{score.streak} подряд</div>
                        )}
                    </div>
                    {score.bestStreak > 1 && (
                        <div className={styles.bestStreak}>рекорд: {score.bestStreak}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TrainingScreen;
